import { getDeferredDeepLinkFingerprint } from "@juisci/device-fingerprint";
import { z } from "zod";
import { requestInstallContextJson } from "./installContextHttp";

const fingerprintMatchResponseSchema = z.object({
  matchType: z.enum(["client", "server"]),
  destinationUrl: z.string().min(1),
  expiresAt: z.union([z.string().min(1), z.date(), z.null()]),
});
type FingerprintMatchResponse = z.infer<typeof fingerprintMatchResponseSchema>;

export interface FingerprintMatchResult {
  matchType: FingerprintMatchResponse["matchType"];
  destinationUrl: FingerprintMatchResponse["destinationUrl"];
  expiresAt: Date | null;
}

/**
 * Matches a user fingerprint to retrieve destination URL for deferred deep linking
 * This should be called when the app boots for the first time after installation
 */
export async function matchUserFingerprint(): Promise<FingerprintMatchResult | null> {
  try {
    const { fingerprint: clientHash } = await getDeferredDeepLinkFingerprint();

    const body = await requestInstallContextJson<FingerprintMatchResponse>(
      "/webapp/public/install-context/match",
      {
        clientHash,
      },
      (raw) => fingerprintMatchResponseSchema.parse(raw),
      { allowNotFound: true }
    );
    if (body === null) {
      return null;
    }

    const expiresAt =
      body.expiresAt === null
        ? null
        : body.expiresAt instanceof Date
          ? body.expiresAt
          : new Date(body.expiresAt);

    return {
      matchType: body.matchType,
      destinationUrl: body.destinationUrl,
      expiresAt,
    };
  } catch (error) {
    const message =
      error instanceof Error
        ? `${error.name}: ${error.message}`
        : String(error);
    console.warn(`DDL: fingerprint match failed error="${message}"`);
    return null;
  }
}
